/*eslint max-lines-per-function: ["error", 300]*/
import { DefaultApi } from '../autogenerated/spdApiClient/apis/DefaultApi';
import {
  CreateUpdateJudgmentDto,
  CreateUpdateJudgmentDtoFromJSON,
} from '../autogenerated/spdApiClient/models/CreateUpdateJudgmentDto';
import { GetJudgmentDto } from '../autogenerated/spdApiClient/models/GetJudgmentDto';
import { InactivateJudgmentDto } from '../autogenerated/spdApiClient/models/InactivateJudgmentDto';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { judgmentForGrid } from '../../../spd/components/Judgments/JudgmentsSearch';
import {
  GetJudgmentWithSignedInfoDto,
  GetLastEditorDto,
} from '../autogenerated/spdApiClient';
import { validateAgainst } from '../validation/validateAgainst';
import { JudgmentSchema } from '../validation/schemas';
import { judgmentForExternalReferralsForGrid } from '../../../spd/components/Judgments/JudgmentsForExternalRefferalsSearch';

export type JudgmentEntity = GetJudgmentDto;

export interface JudgmentContextInterface {
  get: (id: string) => Promise<JudgmentEntity>;
  getForExam: (id: string) => Promise<GetJudgmentWithSignedInfoDto[]>;
  getForRegisterByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    registerId?: string,
    dateFrom?: Date,
    dateTo?: Date,
    userUid?: string,
    healthcareFacilityId?: string,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  getForRegisterCount: (
    filterText?: string,
    registerId?: string,
    dateFrom?: Date,
    dateTo?: Date,
    userUid?: string,
    healthcareFacilityId?: string,
  ) => Promise<number>;
  getForSigningByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    userUid?: string,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  getForSigningCount: (
    filterText?: string,
    userUid?: string,
  ) => Promise<number>;
  getWithoutElectronicSigningByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    userUid?: string,
    dateFrom?: Date,
    dateTo?: Date,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  getWithoutElectronicSigningCount: (
    filterText?: string,
    filterColumns?: string,
    userUid?: string,
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<number>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  getByUserCompanyByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getByUserCompanyCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  create: (dto: CreateUpdateJudgmentDto) => Promise<GetJudgmentDto>;
  update: (id: string, dto: CreateUpdateJudgmentDto) => Promise<void>;
  changeToInactive: (
    id: string,
    reason?: string,
  ) => Promise<InactivateJudgmentDto>;
  getAllForUserRegisterToSign: (
    userUid: string,
    registerId?: string,
  ) => Promise<GetJudgmentDto[]>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  getAllAutomaticByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    setRefresh?: () => void,
  ) => Promise<GridRecord[]>;
  getAllAutomaticCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getNextExamDate: (
    examId: string,
    options?: {
      articleId?: string;
      articleMode?: string;
      pdfTemplateType?: string;
      contactWithFood?: boolean;
      referenceDate?: Date;
    },
  ) => Promise<Date | null>;
}

export const JudgmentContext = (api: DefaultApi) => {
  return {
    get: async (id: string) => {
      return await api.judgmentControllerGet(id);
    },

    getForExam: async (id: string) => {
      return await api.judgmentControllerGetForExam(id);
    },

    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      setRefresh?: () => void,
    ) => {
      const data = await api.judgmentControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(judgment => judgmentForGrid(judgment, setRefresh));
    },

    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.judgmentControllerGetAllCount(filterText || '', filterColumns || ''),

    getByUserCompanyByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.judgmentControllerGetByUserCompanyByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(judgment =>
        judgmentForExternalReferralsForGrid(judgment),
      );
    },

    getByUserCompanyCount: (filterText?: string, filterColumns?: string) =>
      api.judgmentControllerGetByUserCompanyCount(
        filterText || '',
        filterColumns || '',
      ),

    getForRegisterByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      registerId?: string,
      dateFrom?: Date,
      dateTo?: Date,
      userUid?: string,
      healthcareFacilityId?: string,
      setRefresh?: () => void,
    ) => {
      const data = await api.judgmentControllerGetForRegisterByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        registerId || '',
        dateFrom,
        dateTo,
        userUid,
        healthcareFacilityId,
      );
      return data.map(judgment => judgmentForGrid(judgment, setRefresh));
    },

    getForRegisterCount: (
      filterText?: string,
      registerId?: string,
      dateFrom?: Date,
      dateTo?: Date,
      userUid?: string,
      healthcareFacilityId?: string,
    ) =>
      api.judgmentControllerGetForRegisterCount(
        filterText || '',
        registerId || '',
        dateFrom,
        dateTo,
        userUid,
        healthcareFacilityId,
      ),

    getForSigningByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      userUid?: string,
      setRefresh?: () => void,
    ) => {
      const data = await api.judgmentControllerGetForSigningByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        userUid,
      );
      return data.map(judgment => judgmentForGrid(judgment, setRefresh));
    },

    getForSigningCount: (filterText?: string, userUid?: string) =>
      api.judgmentControllerGetForSigningCount(filterText || '', userUid),

    getWithoutElectronicSigningByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      userUid?: string,
      dateFrom?: Date,
      dateTo?: Date,
      setRefresh?: () => void,
    ) => {
      const data =
        await api.judgmentControllerGetWithoutElectronicSigningByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns || '',
          userUid,
          dateFrom,
          dateTo,
        );
      return data.map(judgment => judgmentForGrid(judgment, setRefresh));
    },
    getWithoutElectronicSigningCount: (
      filterText?: string,
      filterColumns?: string,
      userUid?: string,
      dateFrom?: Date,
      dateTo?: Date,
    ) => {
      return api.judgmentControllerGetWithoutElectronicSigningCount(
        filterText || '',
        filterColumns || '',
        userUid,
        dateFrom,
        dateTo,
      );
    },

    create: (dto: CreateUpdateJudgmentDto) =>
      api.judgmentControllerCreate(dto as GetJudgmentDto),

    update: (id: string, dto: CreateUpdateJudgmentDto) =>
      api.judgmentControllerUpdate(id, dto),

    changeToInactive: (id: string, reason?: string) =>
      api.judgmentControllerChangeToInactive(id, { reason }),

    getAllForUserRegisterToSign: (userUid: string, registerId?: string) =>
      api.judgmentControllerGetAllForUserRegisterToSign(userUid, registerId),

    getLastEditor: (id: string) => api.judgmentControllerGetLastEditor(id),

    getAllAutomaticByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      setRefresh?: () => void,
    ) => {
      const data = await api.judgmentControllerGetAllAutomaticByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(judgment => judgmentForGrid(judgment, setRefresh));
    },

    getAllAutomaticCount: (filterText?: string, filterColumns?: string) =>
      api.judgmentControllerGetAllAutomaticCount(
        filterText || '',
        filterColumns || '',
      ),

    getNextExamDate: async (
      examId: string,
      options: {
        articleId?: string;
        articleMode?: string;
        pdfTemplateType?: string;
        contactWithFood?: boolean;
        referenceDate?: Date;
      } = {},
    ): Promise<Date | null> => {
      const result = await api.judgmentControllerGetNextExamDate(
        examId,
        options.articleId,
        options.articleMode,
        options.pdfTemplateType,
        options.contactWithFood !== undefined
          ? String(options.contactWithFood)
          : undefined,
        options.referenceDate?.toISOString(),
      );
      return result ? new Date(result as unknown as string) : null;
    },
  };
};

export const validate = (judgment: CreateUpdateJudgmentDto) => {
  return validateAgainst(JudgmentSchema, judgment);
};

export const judgmentData = (
  judgment?: GetJudgmentDto,
): CreateUpdateJudgmentDto => {
  return CreateUpdateJudgmentDtoFromJSON({
    ...judgment,
    ...{
      psychologistUID: judgment?.psychologist?.uid,
      articleId: judgment?.article?.id,
      examId: judgment?.exam?.id,
    },
  });
};
